export interface Position {
  x: number;
  y: number;
}

export type FactionType = 'United States Of America' | '3rd Reich' | 'United Kingdom' | 'USSR' | 'Imperial Japan' | 'Canada' | 'Fascist Italy' | 'France' | 'Greece';

export enum CityLevel {
  OUTPOST = 1,
  FACTORY = 2,
  AIRBASE = 3
}

export interface CityUpgrade {
  toLevel: CityLevel;
  turnsRemaining: number;
}

export interface City {
  name: string;
  faction: FactionType | null;
  tiles: Position[]; // All tiles that belong to this city
  unitsProduced: string[]; // Track unit IDs produced by this city
  maxUnits: number; // Maximum units this city can produce
  trainingQueue?: TrainingUnit[]; // Units currently being trained
  captureProgress?: {
    faction: FactionType;
    startedTurn: number;
  };
  level: CityLevel; // Current city level
  upgradeInProgress?: CityUpgrade; // Current upgrade if any
}

export interface TrainingUnit {
  type: UnitType;
  turnsRemaining: number;
  tile: Position; // Where the unit will spawn
  queueId?: string; // ID to track reserved slot
}

export interface Tile {
  x: number;
  y: number;
  terrain: TerrainType;
  city?: City;
  mine?: {
    owner: FactionType;  // The faction that placed the mine
    id: string;          // Unique mine ID for tracking
  };
}

export enum TerrainType {
  // Urban terrain (special - for cities only)
  URBAN = 'urban',
  
  // Normandy Campaign
  OPEN_FIELDS = 'open_fields',
  BOCAGE = 'bocage', 
  DENSE_WOODS = 'dense_woods',
  
  // Tunisia Campaign
  DESERT_PLAINS = 'desert_plains',
  ROCKY_HIGHLANDS = 'rocky_highlands',
  SHIFTING_DUNES = 'shifting_dunes',
  
  // Stalingrad Campaign
  FROZEN_STEPPE = 'frozen_steppe',
  SNOW_DRIFTS = 'snow_drifts',
  FROZEN_MARSHES = 'frozen_marshes',
  
  // Okinawa Campaign
  RICE_PADDIES = 'rice_paddies',
  CORAL_RIDGES = 'coral_ridges',
  DENSE_JUNGLE = 'dense_jungle',
  
  // Battle of France Campaign
  FRENCH_COUNTRYSIDE = 'french_countryside',
  ROLLING_HILLS = 'rolling_hills',
  DENSE_FORESTS = 'dense_forests',
  
  // Campobasso Campaign
  VALLEY_FLOORS = 'valley_floors',
  WOODED_HILLS = 'wooded_hills',
  MOUNTAIN_RIDGES = 'mountain_ridges',
  
  // Albania 1940 Campaign
  COASTAL_LOWLANDS = 'coastal_lowlands',
  ALBANIAN_HIGHLANDS = 'albanian_highlands',
  PINDUS_MOUNTAINS = 'pindus_mountains'
}

export interface Camera {
  x: number;
  y: number;
  zoom: number;
}

export enum UnitType {
  // US Units
  RIFLE_SQUAD = 'rifle_squad',
  RANGER_SQUAD = 'ranger_squad',
  AIRBORNE_SQUAD = 'airborne_squad',
  SHERMAN_SECTION = 'sherman_section',
  TANK_DESTROYER_SECTION = 'tank_destroyer_section',
  US_ARTILLERY_SECTION = 'us_artillery_section',
  ENGINEER_SQUAD = 'engineer_squad',
  M8_GREYHOUND = 'm8_greyhound',
  
  // German Units (Normandy)
  PANZERGRENADIER_SQUAD = 'panzergrenadier_squad',
  WAFFEN_SS_SQUAD = 'waffen_ss_squad',
  FALLSCHIRMJAGER_SQUAD = 'fallschirmjager_squad',
  PANZER_IV_ZUG = 'panzer_iv_zug',
  TIGER_I_SECTION = 'tiger_i_section',
  STUG_III_SECTION = 'stug_iii_section',
  GER_ARTILLERY_SECTION = 'ger_artillery_section',
  PIONEER_SQUAD = 'pioneer_squad',
  SDKFZ_222 = 'sdkfz_222',
  
  // German Units (Stalingrad - Eastern Wehrmacht)
  GRENADIER_SQUAD_1942 = 'grenadier_squad_1942',
  STURM_ABTEILUNG_SQUAD = 'sturm_abteilung_squad',
  FALLSCHIRMJAGER_SQUAD_1942 = 'fallschirmjager_squad_1942',
  PANZERGRENADIER_SQUAD_1942 = 'panzergrenadier_squad_1942',
  STURMPIONIERE = 'sturmpioniere',
  SANITATER_SQUAD_1942 = 'sanitater_squad_1942',
  PANZER_IV_AUSF_F2 = 'panzer_iv_ausf_f2',
  PANZER_III_AUSF_L = 'panzer_iii_ausf_l',
  STUG_III_AUSF_F = 'stug_iii_ausf_f',
  PAK_38_BATTERY = 'pak_38_battery',
  SDKFZ_250_1 = 'sdkfz_250_1',
  JU_87_D_STUKA = 'ju_87_d_stuka',
  HE_111_H6 = 'he_111_h6',
  OPEL_BLITZ_1942 = 'opel_blitz_1942',
  SDKFZ_251_1942 = 'sdkfz_251_1942',
  
  // UK Units (Tunisia)
  INFANTRY_SECTION = 'infantry_section',
  GUARDS_INFANTRY = 'guards_infantry',
  COMMANDO_SECTION = 'commando_section',
  SAPPER_SECTION = 'sapper_section',
  CRUSADER_TANK = 'crusader_tank',
  MATILDA_II = 'matilda_ii',
  SIX_POUNDER_AT = 'six_pounder_at',
  TWENTY_FIVE_POUNDER = 'twenty_five_pounder',
  DAIMLER_DINGO = 'daimler_dingo',
  
  // German Units (Afrika Korps - Tunisia)
  SCHUTZEN_SQUAD = 'schutzen_squad',
  AFRIKA_GRENADIERS = 'afrika_grenadiers',
  AK_PIONEER_SQUAD = 'ak_pioneer_squad',
  AK_FALLSCHIRMJAGER = 'ak_fallschirmjager',
  PANZER_III = 'panzer_iii',
  PANZER_II = 'panzer_ii',
  EIGHTY_EIGHT_FLAK = 'eighty_eight_flak',
  PAK_38 = 'pak_38',
  SDKFZ_250_9 = 'sdkfz_250_9',
  
  // Soviet Units (Stalingrad)
  RED_ARMY_SQUAD = 'red_army_squad',
  CONSCRIPT_SQUAD = 'conscript_squad',
  SHOCK_TROOPS = 'shock_troops',
  T34_76 = 't34_76',
  T70_LIGHT_TANK = 't70_light_tank',
  KV1_HEAVY_TANK = 'kv1_heavy_tank',
  BA64_ARMORED_CAR = 'ba64_armored_car',
  ZIS3_AT_GUN = 'zis3_at_gun',
  KATYUSHA_BATTERY = 'katyusha_battery',
  SOVIET_SAPPER_SQUAD = 'soviet_sapper_squad',
  
  // US Marine Units (Iwo Jima)
  MARINE_SQUAD = 'marine_squad',
  MARINE_RAIDERS = 'marine_raiders',
  MARINE_FLAME_TEAM = 'marine_flame_team',
  MARINE_ENGINEERS = 'marine_engineers',
  M4A3_SHERMAN = 'm4a3_sherman',
  M3_STUART = 'm3_stuart',
  M7_PRIEST = 'm7_priest',
  PACK_HOWITZER = 'pack_howitzer',
  
  // Imperial Japan Units (Okinawa)
  IJA_SQUAD = 'ija_squad',
  SNLF_SQUAD = 'snlf_squad',
  ASSAULT_PIONEERS = 'assault_pioneers',
  CAVE_DEFENSE_SQUAD = 'cave_defense_squad',
  TYPE_95_HA_GO = 'type_95_ha_go',
  TYPE_97_CHI_HA = 'type_97_chi_ha',
  TYPE_4_CHI_TO = 'type_4_chi_to',
  TYPE_92_JYU_SOKOSHA = 'type_92_jyu_sokosha',
  TYPE_92_BATTALION_GUN = 'type_92_battalion_gun',
  TYPE_89_MORTAR = 'type_89_mortar',
  TYPE_1_47MM_AT_GUN = 'type_1_47mm_at_gun',
  TYPE_96_150MM_HOWITZER = 'type_96_150mm_howitzer',
  
  // Aircraft Units
  // US Aircraft
  P51_MUSTANG = 'p51_mustang',
  B24_LIBERATOR = 'b24_liberator',
  
  // German Aircraft
  BF109_FIGHTER = 'bf109_fighter',
  JU88_BOMBER = 'ju88_bomber',
  
  // British Aircraft
  SPITFIRE_MK_V = 'spitfire_mk_v',
  LANCASTER_BOMBER = 'lancaster_bomber',
  
  // Soviet Aircraft
  YAK_3_FIGHTER = 'yak_3_fighter',
  IL2_STURMOVIK = 'il2_sturmovik',
  
  // Japanese Aircraft
  A6M_ZERO = 'a6m_zero',
  G4M_BETTY = 'g4m_betty',
  
  // Canadian Units (Campobasso)
  CANADIAN_INFANTRY_SECTION = 'canadian_infantry_section',
  PPCLI = 'ppcli', // Princess Patricia's Canadian Light Infantry
  HIGHLAND_REGIMENT = 'highland_regiment',
  COMBAT_ENGINEERS = 'combat_engineers',
  RAM_TANK = 'ram_tank',
  GRIZZLY_CRUISER = 'grizzly_cruiser',
  SEXTON_SPG = 'sexton_spg',
  TWENTY_FIVE_POUNDER_BATTERY = 'twenty_five_pounder_battery',
  RECONNAISSANCE_PATROL = 'reconnaissance_patrol',
  SHERMAN_FIREFLY = 'sherman_firefly',
  
  // Canadian Aircraft
  SPITFIRE_FIGHTER = 'spitfire_fighter',
  MOSQUITO_FIGHTER_BOMBER = 'mosquito_fighter_bomber',
  
  // Italian Units (Campobasso)
  BERSAGLIERI_SQUAD = 'bersaglieri_squad',
  ALPINI_MOUNTAIN_TROOPS = 'alpini_mountain_troops',
  FOLGORE_PARATROOPERS = 'folgore_paratroopers',
  GENIO_ENGINEERS = 'genio_engineers',
  M15_42_TANK = 'm15_42_tank',
  SEMOVENTE_75_18 = 'semovente_75_18',
  CANNONE_75_32 = 'cannone_75_32',
  OBICE_149_19 = 'obice_149_19',
  ARDITI_SCOUTS = 'arditi_scouts',
  P26_40_HEAVY_TANK = 'p26_40_heavy_tank',
  
  // Italian Aircraft
  MACCHI_C205 = 'macchi_c205',
  SM79_BOMBER = 'sm79_bomber',
  
  // New Units - Airborne additions
  RED_DEVILS_PARACHUTE = 'red_devils_parachute',
  VDV_GUARDS_AIRBORNE = 'vdv_guards_airborne',
  CANADIAN_PARACHUTE_BATTALION = 'canadian_parachute_battalion',
  MARINE_PARACHUTE_REGIMENT = 'marine_parachute_regiment',
  TEISHIN_SHUDAN = 'teishin_shudan',
  
  // New Units - Unique Elite additions
  HEAVY_WEAPONS_SQUAD = 'heavy_weapons_squad',
  GEBIRGSJAGER_SQUAD = 'gebirgsjager_squad',
  BRANDENBURG_COMMANDOS = 'brandenburg_commandos',
  SAN_MARCO_MARINES = 'san_marco_marines',
  
  // Medic Units
  MEDICAL_CORPS_SQUAD = 'medical_corps_squad',
  NAVY_CORPSMEN = 'navy_corpsmen',
  FIELD_AMBULANCE_SECTION = 'field_ambulance_section',
  MEDICAL_BATTALION = 'medical_battalion',
  SANITATER_SQUAD = 'sanitater_squad',
  AK_SANITATER = 'ak_sanitater',
  MEDICAL_SERVICE_CORPS = 'medical_service_corps',
  SANITA_MILITARE = 'sanita_militare',
  IJA_MEDICAL_CORPS = 'ija_medical_corps',
  
  // Transport Aircraft
  C47_SKYTRAIN = 'c47_skytrain',
  R4D_SKYTRAIN = 'r4d_skytrain',
  STIRLING_TRANSPORT = 'stirling_transport',
  LI2_TRANSPORT = 'li2_transport',
  JU52_TRANSPORT = 'ju52_transport',
  JU52_AFRIKA = 'ju52_afrika',
  DAKOTA_TRANSPORT = 'dakota_transport',
  SM82_MARSUPIALE = 'sm82_marsupiale',
  KI57_TRANSPORT = 'ki57_transport',
  
  // Ground Transport Vehicles - Trucks
  GMC_CCKW = 'gmc_cckw',
  OPEL_BLITZ = 'opel_blitz',
  STEYR_1500A = 'steyr_1500a',
  ZIS_5 = 'zis_5',
  BEDFORD_OYD = 'bedford_oyd',
  ISUZU_TYPE_94 = 'isuzu_type_94',
  CMP_TRUCK = 'cmp_truck',
  FIAT_626 = 'fiat_626',
  DUKW_AMPHIBIOUS = 'dukw_amphibious',
  
  // Ground Transport Vehicles - Armored
  M3_HALFTRACK = 'm3_halftrack',
  SDKFZ_251 = 'sdkfz_251',
  SDKFZ_251_AFRIKA = 'sdkfz_251_afrika',
  M3A1_SCOUT_CAR = 'm3a1_scout_car',
  LOYD_CARRIER = 'loyd_carrier',
  TYPE_1_HO_HA = 'type_1_ho_ha',
  PRIEST_KANGAROO = 'priest_kangaroo',
  FIAT_SPA_DOVUNQUE = 'fiat_spa_dovunque',
  LVT_4_AMTRAC = 'lvt_4_amtrac',
  
  // France 1940 Units
  FRENCH_INFANTRY_SQUAD = 'french_infantry_squad',
  CHASSEURS_ALPINS = 'chasseurs_alpins',
  FOREIGN_LEGION_SQUAD = 'foreign_legion_squad',
  COLONIAL_TROOPS = 'colonial_troops',
  FRENCH_ENGINEERS = 'french_engineers',
  FRENCH_MEDICAL_CORPS = 'french_medical_corps',
  CHAR_B1_BIS = 'char_b1_bis',
  SOMUA_S35 = 'somua_s35',
  HOTCHKISS_H39 = 'hotchkiss_h39',
  CANON_75MM = 'canon_75mm',
  CANON_47MM_AT = 'canon_47mm_at',
  FRENCH_RECON = 'french_recon',
  MS_406_FIGHTER = 'ms_406_fighter',
  POTEZ_63_BOMBER = 'potez_63_bomber',
  RENAULT_AHS = 'renault_ahs',
  LORRAINE_38L = 'lorraine_38l',
  
  // Wehrmacht 1940 Units
  WEHRMACHT_INFANTRY_1940 = 'wehrmacht_infantry_1940',
  STOSSTRUPPEN_1940 = 'stosstruppen_1940',
  GEBIRGSJAGER_1940 = 'gebirgsjager_1940',
  MOTORIZED_INFANTRY_1940 = 'motorized_infantry_1940',
  PIONIERE_1940 = 'pioniere_1940',
  SANITATER_1940 = 'sanitater_1940',
  PANZER_III_E = 'panzer_iii_e',
  PANZER_II_C = 'panzer_ii_c',
  PANZER_38T = 'panzer_38t',
  STUG_III_A = 'stug_iii_a',
  PAK_36_BATTERY = 'pak_36_battery',
  AUFKLARUNG_1940 = 'aufklarung_1940',
  BF_109E_FIGHTER = 'bf_109e_fighter',
  JU_87B_STUKA = 'ju_87b_stuka',
  OPEL_BLITZ_1940 = 'opel_blitz_1940',
  SDKFZ_251_AUSF_A = 'sdkfz_251_ausf_a',
  
  // Italian 1940 Units
  REGULAR_INFANTRY_1940 = 'regular_infantry_1940',
  ALPINI_1940 = 'alpini_1940',
  BLACKSHIRT_BATTALION = 'blackshirt_battalion',
  LIBYAN_COLONIAL_TROOPS = 'libyan_colonial_troops',
  GENIO_SAPPERS_1940 = 'genio_sappers_1940',
  REGIO_MEDICI = 'regio_medici',
  L3_33_TANKETTE = 'l3_33_tankette',
  M11_39_TANK = 'm11_39_tank',
  L6_40_LIGHT_TANK = 'l6_40_light_tank',
  CANNONE_47_32 = 'cannone_47_32',
  OBICE_100_17 = 'obice_100_17',
  AB_40_ARMORED_CAR = 'ab_40_armored_car',
  CR_42_FALCO = 'cr_42_falco',
  SM_79_SPARVIERO = 'sm_79_sparviero',
  FIAT_SPA_AUTOPROTETTO = 'fiat_spa_autoprotetto',
  
  // Greek 1940 Units
  EVZONES_REGIMENT = 'evzones_regiment',
  REGULAR_INFANTRY_DIVISION = 'regular_infantry_division',
  CRETAN_DIVISION = 'cretan_division',
  NATIONAL_GUARD_BATTALION = 'national_guard_battalion',
  ENGINEER_COMPANY = 'engineer_company',
  MEDICAL_SECTION = 'medical_section',
  VICKERS_6TON_TANK = 'vickers_6ton_tank',
  FT_17_RENAULT = 'ft_17_renault',
  CARDEN_LOYD_TANKETTE = 'carden_loyd_tankette',
  CAPTURED_L3_TANKETTE = 'captured_l3_tankette',
  SCHNEIDER_75MM_MOUNTAIN_GUN = 'schneider_75mm_mountain_gun',
  SKODA_75MM_FIELD_GUN = 'skoda_75mm_field_gun',
  PZL_P24_FIGHTER = 'pzl_p24_fighter',
  FAIREY_BATTLE_BOMBER = 'fairey_battle_bomber',
  FORD_V8_TRUCK = 'ford_v8_truck',
  DODGE_T110L_HALFTRACK = 'dodge_t110l_halftrack'
}

export enum UnitCategory {
  INFANTRY = 'infantry',
  ARMOR = 'armor',
  ARTILLERY = 'artillery',
  RECONNAISSANCE = 'reconnaissance',
  AIRCRAFT = 'aircraft',
  FIGHTERS = 'fighters',
  BOMBERS = 'bombers',
  TRANSPORT_AIRCRAFT = 'transport_aircraft',
  ENGINEERS = 'engineers',
  MEDICS = 'medics',
  TRANSPORT = 'transport',
  HALFTRACK = 'halftrack'
}

export interface UnitStats {
  movement: number;
  attack: number;
  defense: number;
  health: number;
  maxHealth: number;
  range: number;
  cost: number;
}

export interface Unit {
  id: string;
  name: string;
  type: UnitType;
  faction: FactionType;
  position: Position;
  stats: UnitStats;
  movementRemaining: number;
  hasAttacked: boolean;
  hometown?: string; // Name of the city that produced this unit
  category: UnitCategory;
  level: number;
  // Veteran system
  killCount?: number; // Number of enemy units destroyed
  isVeteran?: boolean; // Has achieved veteran status
  veteranPromotedTurn?: number; // Turn when promoted to veteran
  originalStats?: UnitStats; // Store original stats before veteran bonus
  // Animation properties
  displayPosition?: Position; // Current display position for smooth animation
  targetPosition?: Position; // Target position for animation
  animationStartTime?: number; // When the animation started
  veteranPromotionAnimation?: {
    startTime: number;
    duration: number;
  };
  // Combat animation properties
  attackAnimation?: {
    type: 'attacker' | 'defender';
    startTime: number;
    duration: number;
  };
  // Damage text properties
  damageText?: {
    amount: number;
    startTime: number;
    duration: number;
  };
  // Death animation properties
  deathAnimation?: {
    startTime: number;
    duration: number;
    startY: number;
  };
  // Transport/Airborne properties
  carryingUnit?: UnitType; // For transports: what airborne unit they're carrying (single unit for aircraft/halftracks)
  carryingUnits?: UnitType[]; // For trucks: multiple units they're carrying
  
  // Status effects
  shellshocked?: {
    turnsRemaining: number; // Number of turns left (starts at 3)
    appliedTurn: number; // Turn when shellshocked was applied
  };
  carryingUnitData?: Unit; // Store the actual unit data when boarding
  carryingUnitsData?: Unit[]; // Store actual unit data for trucks
  isTransport?: boolean; // Flag to identify transport aircraft
  
  // Towing properties
  towingUnit?: UnitType; // Unit type being towed (for transport vehicles)
  towingUnitData?: Unit; // Store the actual artillery unit data when towing
  
  // Turn-based action flags
  hasBoarded?: boolean; // Whether unit has boarded/disembarked this turn
  hasTowed?: boolean; // Whether transport has towed/untowed this turn
}

export enum CampaignType {
  NORMANDY = 'normandy',
  TUNISIA = 'tunisia',
  STALINGRAD = 'stalingrad',
  OKINAWA = 'okinawa',
  CAMPOBASSO = 'campobasso',
  TUTORIAL = 'tutorial',
  FRANCE_1940 = 'france_1940',
  ALBANIA_1940 = 'albania_1940'
}

export interface BattleStats {
  unitsCreated: number;
  unitsLost: number;
  enemiesKilled: number;
  citiesCaptured: number;
  citiesLost: number;
  resourcesSpent: number;
  resourcesEarned: number;
  damageDealt: number;
  damageTaken: number;
  unitsHealed: number;
  unitsRepaired: number;
  minesPlaced: number;
  minesDetonated: number;
}

export interface GameState {
  map: Tile[][];
  camera: Camera;
  selectedTile: Position | null;
  units: Map<string, Unit>;
  selectedUnit: string | null;
  currentFaction: FactionType;
  resources: Map<string, number>;
  turn: number;
  pendingNotifications?: Map<string, string[]>; // faction -> notifications
  campaign?: CampaignType;
  gameMode?: 'PvP' | 'PvE' | 'Online'; // Player vs Player, Player vs AI, or Online PvP
  aiControlledFaction?: FactionType; // Which faction is AI controlled in PvE mode
  battleStats?: Map<FactionType, BattleStats>; // Track statistics for each faction
}